import React, { useState, useEffect } from "react";
import axios from "axios";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

/**
 * Quiz popup modal: add or edit a quiz question.
 * Props: isOpen, onClose, lessonId, editContent (null for new), API_URL, onSuccess (called after save).
 */
function QuizPop({ isOpen, onClose, lessonId, editContent, API_URL, onSuccess }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [options, setOptions] = useState([
    { text: "", desc: "" },
    { text: "", desc: "" },
    { text: "", desc: "" },
    { text: "", desc: "" },
  ]);
  const [correctAnswer, setCorrectAnswer] = useState(0);
  const [correctOptionDesc, setCorrectOptionDesc] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (editContent) {
      let parsed = null;
      try {
        parsed = JSON.parse(editContent.content || "{}");
      } catch (e) {}
      let opts = [
        { text: "", desc: "" },
        { text: "", desc: "" },
        { text: "", desc: "" },
        { text: "", desc: "" },
      ];
      let correct = 0;
      let correctDesc = "";
      if (parsed && Array.isArray(parsed.options)) {
        opts = parsed.options.map((o) =>
          typeof o === "string" ? { text: o, desc: "" } : o
        );
        while (opts.length < 4) opts.push({ text: "", desc: "" });
        correct = parsed.correctAnswer ?? 0;
        correctDesc = parsed.correctOptionDesc || "";
      }
      setTitle(parsed?.question || editContent.title || "");
      setDescription(parsed?.description || editContent.description || "");
      setOptions(opts);
      setCorrectAnswer(correct);
      setCorrectOptionDesc(correctDesc);
    } else {
      setTitle("");
      setDescription("");
      setOptions([
        { text: "", desc: "" },
        { text: "", desc: "" },
        { text: "", desc: "" },
        { text: "", desc: "" },
      ]);
      setCorrectAnswer(0);
      setCorrectOptionDesc("");
    }
  }, [isOpen, editContent]);

  const handleOptionChange = (idx, value, field = "text") => {
    setOptions((prev) =>
      prev.map((opt, i) =>
        i === idx ? { ...opt, [field]: value } : opt
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!lessonId || !title.trim()) {
      toast.error("Question is required.");
      return;
    }
    setSaving(true);
    try {
      const contentJson = JSON.stringify({
        question: title.trim(),
        options: options.map((o) => (o.text !== undefined ? o.text : o)),
        correctAnswer,
        correctOptionDesc,
        description,
      });
      const payload = {
        lesson_id: lessonId,
        title: title.trim(),
        description: description || "",
        type: "question",
        url: "",
        content: contentJson,
        duration: 0,
      };
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
      if (editContent?.id) {
        await axios.put(
          `${API_URL}/contents/${editContent.id}`,
          payload,
          { headers }
        );
        toast.success("Question updated.");
      } else {
        await axios.post(`${API_URL}/contents`, payload, { headers });
        toast.success("Question added.");
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Quiz save error:", err);
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to save question.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-green-50">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <QuestionMarkCircleIcon className="h-5 w-5 text-green-600" />
            {editContent ? "Edit Quiz" : "Add Quiz"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-green-100 text-gray-600"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <form id="quiz-pop-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter question"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add extra info or explanation for this question (optional)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options (A, B, C, D) *
            </label>
            {options.map((opt, idx) => (
              <div key={idx} className="mb-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <input
                    type="text"
                    value={opt.text !== undefined ? opt.text : opt}
                    onChange={(e) => handleOptionChange(idx, e.target.value, "text")}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                    required
                  />
                  <label className="flex items-center gap-2 sm:gap-1 cursor-pointer">
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={correctAnswer === idx}
                      onChange={() => setCorrectAnswer(idx)}
                      className="rounded-full text-green-600 focus:ring-green-500"
                    />
                    <span className="text-xs text-gray-600 whitespace-nowrap">Correct</span>
                  </label>
                </div>
                {correctAnswer === idx && (
                  <input
                    type="text"
                    value={correctOptionDesc}
                    onChange={(e) => setCorrectOptionDesc(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs mt-2 focus:ring-2 focus:ring-green-200 focus:border-green-300"
                    placeholder={`Explanation for Option ${String.fromCharCode(65 + idx)} (optional)`}
                  />
                )}
              </div>
            ))}
          </div>
        </form>
        <div className="flex justify-end gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="quiz-pop-form"
            disabled={saving}
            className="px-4 py-2 rounded-lg font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuizPop;
